import { Component, Inject, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BetriebskontakteService } from './betriebskontakte.service';
import { MessageService } from '../../../services/message-service/message.service';
import { BetriebSuchResult, Betriebskontakt, isVvvoPlaceholderBezeichnung } from './betriebskontakt';
import { KieneUtils } from '../../../utils/KieneUtils';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';

export interface NeuerKontaktDialogData {
    vvvo: string;
}

export interface NeuerKontaktDialogResult {
    success: boolean;
    kontakt?: Betriebskontakt;
    /** Nach Hinzufügen eines VVVO-Platzhalters Bearbeiten-Dialog öffnen */
    openEdit?: boolean;
}

@Component({
    selector: 'kiene-betriebskontakte-neuer-kontakt-dialog',
    templateUrl: './betriebskontakte-neuer-kontakt-dialog.component.html',
    styleUrls: ['./betriebskontakte-neuer-kontakt-dialog.component.scss'],
})
export class BetriebskontakteNeuerKontaktDialogComponent implements OnInit, AfterViewInit {
    neuerKontaktForm: FormGroup;
    ladeErstelleKontakt = false;

    // Neue Eigenschaften für die Betriebssuche
    dialogModus: 'suche' | 'erstellen' = 'suche';
    ladeBetriebssuche = false;
    gefundeneBetriebe: Betriebskontakt[] = [];
    ausgewaehlterBetrieb: Betriebskontakt | null = null;
    ladeHinzufuegen = false; // Einfacher Status für das Hinzufügen

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<BetriebskontakteNeuerKontaktDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: NeuerKontaktDialogData,
        private betriebskontakteService: BetriebskontakteService,
        private messageService: MessageService,
        private cdr: ChangeDetectorRef,
        private localStorageService: LocalStorageService,
    ) {
        this.neuerKontaktForm = this.fb.group({
            vvvo: ['', [Validators.required, Validators.pattern(/^\d{15}$/)]],
            bezeichnung: ['', [Validators.required]],
            zusatz: [''],
            strasse: [''],
            plz: [''],
            ort: [''],
            email: ['', [Validators.email]],
            telefon_1: [''],
            mobil_1: [''],
            betriebsart_id: [''], // Betriebsart (wird automatisch gesetzt)
            ist_haendler: [false], // Flag für Händler-Status
        });
    }

    ngOnInit(): void {
        if (this.data.vvvo) {
            this.neuerKontaktForm.patchValue({ vvvo: this.data.vvvo });
        }

        // Überwache Änderungen an der Händler-Flag
        this.neuerKontaktForm.get('ist_haendler')?.valueChanges.subscribe(istHaendler => {
            if (istHaendler) {
                // Automatisch Betriebsart für Händler setzen (9002 = Händler)
                this.neuerKontaktForm.patchValue({ betriebsart_id: '9002' });
            } else {
                // Betriebsart auf null setzen wenn Händler-Flag deaktiviert wird
                this.neuerKontaktForm.patchValue({ betriebsart_id: null });
            }
        });

        // Explizite Change Detection nach Initialisierung mit Verzögerung
        setTimeout(() => {
            this.cdr.detectChanges();
        }, 100);
    }

    ngAfterViewInit(): void {
        // Zusätzliche Change Detection nach View-Initialisierung
        setTimeout(() => {
            this.cdr.detectChanges();

            // Automatisch nach Betrieben suchen wenn VVVO bereits vorhanden
            if (this.data.vvvo) {
                this.sucheBetriebe();
            }
        }, 200);
    }

    /**
     * Begrenzt die VVVO-Eingabe auf 15 Zeichen und erlaubt nur Zahlen
     */
    onVvvoInput(event: any): void {
        const input = event.target;
        let value = input.value;

        // Entferne alle nicht-numerischen Zeichen
        value = value.replace(/\D/g, '');

        // Begrenze auf 15 Zeichen
        if (value.length > 15) {
            value = value.substring(0, 15);
        }

        // Aktualisiere den Formularwert
        this.neuerKontaktForm.patchValue({ vvvo: value });

        // Setze den Cursor an das Ende
        setTimeout(() => {
            input.setSelectionRange(value.length, value.length);
        }, 0);

        // Automatische Suche wenn VVVO vollständig eingegeben wurde
        if (value.length === 15) {
            this.sucheBetriebe();
        }
    }

    /**
     * Verhindert die Eingabe von Nicht-Zahlen
     */
    onKeyPress(event: KeyboardEvent): boolean {
        const charCode = event.which ? event.which : event.keyCode;
        // Erlaube nur Zahlen (0-9)
        if (charCode < 48 || charCode > 57) {
            event.preventDefault();
            return false;
        }
        return true;
    }

    /**
     * Verhindert das Einfügen von Nicht-Zahlen
     */
    onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const clipboardData = event.clipboardData;
        if (clipboardData) {
            const pastedText = clipboardData.getData('text');
            // Entferne alle Nicht-Zahlen und begrenze auf 15 Zeichen
            const numbersOnly = pastedText.replace(/[^0-9]/g, '').substring(0, 15);
            if (numbersOnly) {
                this.neuerKontaktForm.patchValue({ vvvo: numbersOnly });
                // Automatische Suche wenn 15 Zahlen eingefügt wurden
                if (numbersOnly.length === 15) {
                    this.sucheBetriebe();
                }
            }
        }
    }

    /**
     * Sucht nach Betrieben basierend auf der eingegebenen VVVO
     */
    sucheBetriebe(): void {
        const vvvo = this.neuerKontaktForm.get('vvvo')?.value;

        if (!vvvo || vvvo.length !== 15) {
            this.gefundeneBetriebe = [];
            return;
        }

        this.ladeBetriebssuche = true;
        this.gefundeneBetriebe = [];

        this.betriebskontakteService.sucheBetriebe(vvvo).subscribe({
            next: response => {
                this.ladeBetriebssuche = false;

                // Konvertiere BetriebSuchResult zu Betriebskontakt
                this.gefundeneBetriebe = (response.records || [])
                    .filter((betrieb: BetriebSuchResult) => !!betrieb.kunde_id)
                    .map((betrieb: BetriebSuchResult) => {
                    return {
                        kontakt_id: Number(betrieb.kontakt_id || 0),
                        benutzer_id: Number(betrieb.benutzer_id || 0),
                        kunde_id: betrieb.kunde_id!,
                        favorit: Number(betrieb.favorit || 0),
                        ist_kunde: Number(betrieb.ist_kunde || 0),
                        bezeichnung: betrieb.bezeichnung,
                        zusatz: betrieb.zusatz,
                        vvvo: betrieb.vvvo,
                        email: betrieb.email,
                        telefon_1: betrieb.telefon_1,
                        telefon_2: betrieb.telefon_2,
                        mobil_1: betrieb.mobil_1,
                        mobil_2: betrieb.mobil_2,
                        strasse: betrieb.strasse,
                        plz: betrieb.plz,
                        ort: betrieb.ort,
                        ortsteil: betrieb.ortsteil,
                        bezeichnung_final: betrieb.bezeichnung_final,
                        zusatz_final: betrieb.zusatz_final,
                        strasse_final: betrieb.strasse_final,
                        plz_final: betrieb.plz_final,
                        ort_final: betrieb.ort_final,
                        ortsteil_final: betrieb.ortsteil_final,
                        betriebsart_id: betrieb.betriebsart_id,
                        betriebsart_bezeichnung: undefined,
                        bereits_kontakt: Number(betrieb.bereits_kontakt || 0),
                        erstellt_von: Number(betrieb.erstellt_von || 0),
                    } as Betriebskontakt;
                });

                // Wenn keine Betriebe gefunden wurden, wechsle automatisch in den Erstellungsmodus
                if (this.gefundeneBetriebe.length === 0) {
                    this.dialogModus = 'erstellen';
                }

                // Change Detection nach der Suche
                this.cdr.detectChanges();
            },
            error: error => {
                this.ladeBetriebssuche = false;
                this.messageService.errorMessage('Fehler bei der Betriebssuche', error);
            },
        });
    }

    /**
     * Wählt einen gefundenen Betrieb aus und fügt ihn als Kontakt hinzu
     */
    waehleBetriebAus(betrieb: Betriebskontakt): void {
        // Einfache Prüfung: Wenn bereits geladen wird, ignoriere
        if (this.ladeHinzufuegen) {
            return;
        }

        // Setze Flag und starte API-Aufruf
        this.ladeHinzufuegen = true;
        this.ausgewaehlterBetrieb = betrieb;

        // Sofortige Change Detection
        this.cdr.detectChanges();

        // Füge den bestehenden Betrieb als Kontakt hinzu
        this.betriebskontakteService.fuegeKontaktHinzu(betrieb.kunde_id, false).subscribe({
            next: response => {
                this.ladeHinzufuegen = false;
                this.cdr.detectChanges();

                if (response && response.fehler) {
                    this.messageService.errorMessage('Fehler beim Hinzufügen des Kontakts', response.fehler);
                    return;
                }

                this.messageService.infoMessage(`Kontakt "${betrieb.bezeichnung}" erfolgreich hinzugefügt`);

                // Aktualisiere die bereits_kontakt Eigenschaft des Betriebs in der Liste
                const betriebIndex = this.gefundeneBetriebe.findIndex(b =>
                    KieneUtils.sameId(b.kunde_id, betrieb.kunde_id),
                );
                if (betriebIndex >= 0) {
                    this.gefundeneBetriebe[betriebIndex].bereits_kontakt = 1;
                }

                const currentUserId = Number(this.localStorageService.getCurrentUser()?.benutzer_id ?? 0);
                const hinzugefuegterKontakt: Betriebskontakt = {
                    ...betrieb,
                    kontakt_id: response.kontakt_id || 0,
                    benutzer_id: response.benutzer_id || currentUserId,
                    favorit: 0,
                    ist_kunde: Number(betrieb.ist_kunde) > 0 ? 1 : 0,
                    bereits_kontakt: 1,
                    erstellt_von: currentUserId,
                };

                this.dialogRef.close({
                    success: true,
                    kontakt: hinzugefuegterKontakt,
                    openEdit: isVvvoPlaceholderBezeichnung(betrieb.bezeichnung),
                });
            },
            error: error => {
                this.ladeHinzufuegen = false;
                this.cdr.detectChanges();
                this.messageService.errorMessage('Fehler beim Hinzufügen des Kontakts', error);
            },
        });
    }

    /**
     * Wechselt zum Erstellungsmodus für einen neuen Betrieb
     */
    erstelleNeuenBetrieb(): void {
        if (this.gefundeneBetriebe.length > 0) {
            this.messageService.alertMessage(
                'Es existiert bereits ein Betrieb mit dieser VVVO. Bitte wählen Sie ihn aus der Liste aus.',
            );
            this.dialogModus = 'suche';
            return;
        }
        this.dialogModus = 'erstellen';
    }

    /**
     * Geht zurück zum Suchmodus
     */
    zurueckZurSuche(): void {
        this.dialogModus = 'suche';
        this.ausgewaehlterBetrieb = null;
    }

    erstelleNeuenKontakt(): void {
        if (this.neuerKontaktForm.valid) {
            if (this.gefundeneBetriebe.length > 0) {
                this.messageService.alertMessage(
                    'Es existiert bereits ein Betrieb mit dieser VVVO. Bitte wählen Sie ihn aus der Suche aus.',
                );
                this.dialogModus = 'suche';
                return;
            }

            this.ladeErstelleKontakt = true;

            const neuerKontakt = {
                vvvo: this.neuerKontaktForm.get('vvvo')?.value,
                bezeichnung: this.neuerKontaktForm.get('bezeichnung')?.value,
                zusatz: this.neuerKontaktForm.get('zusatz')?.value || '',
                strasse: this.neuerKontaktForm.get('strasse')?.value || '',
                plz: this.neuerKontaktForm.get('plz')?.value || '',
                ort: this.neuerKontaktForm.get('ort')?.value || '',
                email: this.neuerKontaktForm.get('email')?.value || '',
                telefon_1: this.neuerKontaktForm.get('telefon_1')?.value || '',
                mobil_1: this.neuerKontaktForm.get('mobil_1')?.value || '',
                betriebsart_id: this.neuerKontaktForm.get('betriebsart_id')?.value,
            };

            this.betriebskontakteService.erstelleBetriebskontakt(neuerKontakt).subscribe({
                next: response => {
                    // Prüfe ob die Antwort einen Fehler enthält
                    if (response && response.fehler) {
                        this.messageService.errorMessage('Fehler beim Erstellen des Kontakts', response.fehler);
                        this.ladeErstelleKontakt = false;
                        return; // Dialog bleibt offen
                    }

                    // Nur bei Erfolg den Dialog schließen
                    this.messageService.infoMessage('Neuer Kontakt erfolgreich erstellt');

                    // Erstelle einen vollständigen Betriebskontakt mit den erstellten Daten
                    const erstellterKontakt: Betriebskontakt = {
                        kontakt_id: response.kontakt_id || 0,
                        benutzer_id: response.benutzer_id || 0,
                        kunde_id: response.kunde_id || 0,
                        favorit: 0,
                        ist_kunde: 1,
                        bezeichnung: neuerKontakt.bezeichnung,
                        zusatz: neuerKontakt.zusatz,
                        vvvo: neuerKontakt.vvvo,
                        email: neuerKontakt.email,
                        telefon_1: neuerKontakt.telefon_1,
                        telefon_2: '',
                        mobil_1: neuerKontakt.mobil_1,
                        mobil_2: '',
                        strasse: neuerKontakt.strasse,
                        plz: neuerKontakt.plz,
                        ort: neuerKontakt.ort,
                        ortsteil: '',
                        bezeichnung_final: neuerKontakt.bezeichnung,
                        zusatz_final: neuerKontakt.zusatz,
                        strasse_final: neuerKontakt.strasse,
                        plz_final: neuerKontakt.plz,
                        ort_final: neuerKontakt.ort,
                        ortsteil_final: '',
                        betriebsart_id: neuerKontakt.betriebsart_id,
                        betriebsart_bezeichnung: undefined,
                        bereits_kontakt: 0,
                    };

                    this.dialogRef.close({ success: true, kontakt: erstellterKontakt });
                },
                error: error => {
                    this.messageService.errorMessage('Fehler beim Erstellen des Kontakts', error);
                    // Dialog bleibt bei Fehlern offen
                    this.ladeErstelleKontakt = false;
                },
            });
        }
    }

    abbrechen(): void {
        this.dialogRef.close(false);
    }
}
